import { ifElse } from 'aa_common/front-end/helpers'
import { getBookInformation } from 'api/app/asset'
import { ASSET_CHANGE_SITUATION_TYPES, DATE_FORMAT_RFC, DEPRECIATION_METHOD_CODE } from 'common/constants'
import { findAccountingLedger, getAccountingLedger } from 'common/helpers/ledger-helper'
import { isEqual } from 'lodash'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { useWatch } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { selectAssetItemData } from 'store/asset/selectors'
import { selectorLedgerSettingsList } from 'store/settings/ledgerSetting/selectors'
import { useDebouncedCallback } from 'use-debounce'

import { useIsFullImpairment } from './use-is-full-impairment'

export const defaultBookInfoValue = { book_value: 0, date: '', accumulated_impaired_amount: 0 } as const

// Ref: https://miro.com/app/board/uXjVOz6KRwc=/?moveToWidget=3458764526445045122&cot=14
export const useImpairmentBookInfo = (impairAtFieldName: string, impairMethodFieldName: string) => {
  const [bookValue, setBookValue] = useState(defaultBookInfoValue)
  const [loading, setLoading] = useState(false)

  const { biid } = useParams<{ biid: string }>()
  const { data: ledgers } = useSelector(selectorLedgerSettingsList, isEqual)

  const watchValues = useWatch({ name: [impairAtFieldName, impairMethodFieldName] })
  const impairedAt = watchValues[impairAtFieldName]
  const depreciation_ended_method = watchValues[impairMethodFieldName]
  const { isFullImpairment } = useIsFullImpairment()

  const debounceGetBookInformation = useDebouncedCallback(
    (isCancelled: boolean, ...args: Parameters<typeof getBookInformation>) => {
      getBookInformation(...args)
        .then(response => {
          if (isCancelled) return
          const bookInfo = response?.data?.data?.items?.[0]
          setBookValue(bookInfo || defaultBookInfoValue)
        })
        .finally(() => setLoading(false))
    },
    300
  )

  useEffect(() => {
    if (!biid || !impairedAt || !depreciation_ended_method) {
      setBookValue(defaultBookInfoValue)
      return
    }

    const ledgersPayload = [
      {
        ledger_setting_id: findAccountingLedger(ledgers)?.id as number,
        depreciation_ended_method,
      },
    ]

    setLoading(true)
    let isCancelled = false

    debounceGetBookInformation(
      isCancelled,
      biid,
      moment(impairedAt).format(DATE_FORMAT_RFC),
      ledgersPayload,
      [],
      ifElse(isFullImpairment, ASSET_CHANGE_SITUATION_TYPES.IMPAIRMENT, ASSET_CHANGE_SITUATION_TYPES.PARTIAL_IMPAIRMENT)
    )

    return () => {
      isCancelled = true
    }
  }, [biid, impairedAt, ledgers, depreciation_ended_method, isFullImpairment])

  return useMemo(() => {
    return [bookValue, loading] as const
  }, [bookValue, loading])
}

// Ref: https://miro.com/app/board/uXjVOz6KRwc=/?moveToWidget=3458764526445045453&cot=14
export const useImpairmentLossAmount = (bookValue: number) => {
  const asset = useSelector(selectAssetItemData, isEqual)

  return useMemo(() => {
    const assetType = asset?.asset_category?.asset_type
    const accountingLedger = getAccountingLedger(asset?.fixed_asset_ledgers)
    const dMethodCode = accountingLedger?.depreciation_method_code

    if (bookValue && assetType && dMethodCode) {
      const isSpecialMethod = [
        DEPRECIATION_METHOD_CODE.AVERAGE_USAGE_PERIOD,
        DEPRECIATION_METHOD_CODE.LEASE_PERIOD_STRAIGHT_LINE,
      ].includes(dMethodCode)

      return (
        bookValue -
        (isSpecialMethod ? (accountingLedger?.residual_amount ?? 0) : (accountingLedger?.memorandum_value ?? 0))
      )
    }

    return 0
  }, [bookValue, asset])
}
